import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext, Field, FieldProps } from 'formik';
import { FormControl, Input, FormHelperText, InputAdornment } from '@material-ui/core';
import { isEmpty, find } from 'lodash';

import { GetSharePreviewInfoQuery } from 'api';
import { useClient, useContent } from 'hooks';

import { INetwork, IShareNetworkAuth } from '../types';
import Count from './Count';
import Note from 'components/Note';

interface IFormValue {
  [name: string]: string;
}

interface IProps {
  network: INetwork;
  previewInfo?: GetSharePreviewInfoQuery['getSharePreviewInfo'];
}

const Commentary: FC<IProps> = ({ network, previewInfo }) => {
  const content = useContent();
  const client = useClient();
  const { values } = useFormikContext();
  const { t } = useTranslation();
  const { provider } = network;

  const lockedCommentary = (!isEmpty(previewInfo) && previewInfo.locked_commentary) || false;
  const placeholder = !lockedCommentary ? `sharing:${network.provider}` : '';
  const { active }: IShareNetworkAuth = values[provider];
  const disclosure = getContentDisclosure();

  if (!active) {
    return null;
  }

  function getContentDisclosure() {
    const contentDisclosure = find(content.disclosures, ['network', network.provider]);
    if (!contentDisclosure) {
      return null;
    }

    const clientDisclosure = find(client.disclosures, ['id', contentDisclosure.disclosure_id]);
    if (!clientDisclosure) {
      return null;
    }

    return clientDisclosure.disclosure;
  }

  return (
    <Fragment>
      <Field name={`${provider}.user_commentary`}>
        {(fieldProps: FieldProps<IFormValue>) => {
          const { errors, touched } = fieldProps.form;
          const { field } = fieldProps;

          const fieldValue = field.value || '';
          const hasError = !!(errors[name] && touched[name]);
          const nameError = touched[name] && errors[name];

          return (
            <FormControl fullWidth error={hasError} margin="dense">
              <Input
                multiline
                disableUnderline
                value={fieldValue}
                placeholder={t(placeholder)}
                onChange={field.onChange}
                inputProps={{ name: field.name }}
                disabled={lockedCommentary}
                endAdornment={
                  <InputAdornment position="end">
                    <Count network={network} />
                  </InputAdornment>
                }
              />
              {nameError && <FormHelperText>{nameError}</FormHelperText>}
            </FormControl>
          );
        }}
      </Field>

      {!!disclosure && <Note color="inherit">{t('sharing:disclosureTip', { disclosure })}</Note>}
      {lockedCommentary && <Note i18nKey="sharing:shareLockdownTip" />}
    </Fragment>
  );
};

export default Commentary;
